import { ElaboratedTicketGroup } from 'types';
import { core, safeToNumber } from '@tock/coretypes';
import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';

import { CalendarItem, CalendarItemStateEnum } from 'utils/calendarItems';
import { CalendarItemSelections, SelectedInventoryItemId } from 'state/calendar';
import { CalendarPopoverOpenPayload } from 'actions/CalendarActions';
import { CalendarSelectionMode } from 'constants/CalendarState';
import { StoreType } from '@tock/shared/utils/connect';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { assign, find, forEach, isEmpty } from 'es-toolkit/compat';
import { isSelectedSlot, isSelectedTableStateRange } from 'utils/timeline/typeGuards';
import { isWalkInCalendarItem } from 'utils/calendarUtils';

export const getFocusedTimeSpanSelector = (state: StoreType) => state.calendar.focusedTimeSpan;

const allCalendarItemsByIdSelector = createSelector(
  (state: StoreType) => state.calendar.calendar,
  (ticketGroups: ElaboratedTicketGroup[]): Dictionary<CalendarItem> => {
    const calendarItemsById: Dictionary<CalendarItem> = {};

    forEach(ticketGroups, (ticketGroup: ElaboratedTicketGroup) => {
      forEach(ticketGroup.calendarItems, (calendarItem: CalendarItem) => {
        calendarItemsById[calendarItem.id] = calendarItem;
      });
    });

    return calendarItemsById;
  }
);

export const calendarSelector = createSelector(
  (state: StoreType) => state.calendar,
  (calendar: StoreType['calendar']) => calendar
);

export const calendarSummarySelector = createSelector(
  (state: StoreType) => state.calendar.monthCalendar,
  (monthCalendar: StoreType['calendar']['monthCalendar']) => monthCalendar
);

export const calendarEntitySelector = createSelector(
  (state: StoreType) => state.calendar.purchases,
  (state: StoreType) => state.calendar.visitsByWalkinId,
  (
    purchases,
    visitsByWalkinId
  ): {
    purchases: StoreType['calendar']['purchases'];
    visitsByWalkinId: StoreType['calendar']['visitsByWalkinId'];
  } => ({
    purchases,
    visitsByWalkinId,
  })
);

export const calendarRequestInProgressSelector = createSelector(
  (state: StoreType) => state.calendar.requestInProgress,
  (requestInProgress: boolean): boolean => requestInProgress
);

export const calendarIsInitializedSelector = createSelector(
  (state: StoreType) => state.calendar.calendarIsInitialized,
  (isInitialized: boolean): boolean => isInitialized
);

export const calendarTextFilterSelector = createSelector(
  (state: StoreType) => state.calendar.textFilter,
  (textFilter: StoreType['calendar']['textFilter']): string | undefined => textFilter
);

export const calendarItemSelectionsSelector = createSelector(
  (state: StoreType) => state.calendar.calendarItemSelections,
  (
    calendarItemSelections: StoreType['calendar']['calendarItemSelections']
  ): CalendarItemSelections => calendarItemSelections
);

export const calendarSelectionModeSelector = createSelector(
  (state: StoreType) => state.calendar.selectionMode,
  (selectionMode: StoreType['calendar']['selectionMode']): CalendarSelectionMode | undefined =>
    selectionMode
);

export const calendarBookingModalVisibleSelector = createSelector(
  (state: StoreType) => state.calendar.bookingModalVisible,
  (bookingModalVisible): boolean => bookingModalVisible
);

export const calendarStateSelector = (state: StoreType) => state.calendar.calendarState;

export const selectedCalendarItemIdSelector = (state: StoreType) =>
  state.calendar.selectedInventoryItemId;

export const calendarSelectedCalendarItemSelector = createSelector(
  allCalendarItemsByIdSelector,
  selectedCalendarItemIdSelector,
  (state: StoreType) => state.booking.ticketGroup,
  (
    calendarItemsById: Dictionary<CalendarItem>,
    calendarItemId: SelectedInventoryItemId | undefined,
    ticketGroup: core.TicketGroup | undefined
  ): CalendarItem | undefined => {
    if (!calendarItemId) {
      return undefined;
    }
    let calendarItem: CalendarItem | undefined;
    switch (calendarItemId.type) {
      case 'WALK_IN':
        calendarItem = find(calendarItemsById, (calendarItem: CalendarItem) => {
          if (isWalkInCalendarItem(calendarItem)) {
            return calendarItem.party.id === calendarItemId.partyId;
          } else {
            return false;
          }
        });
        break;
      case CalendarItemStateEnum.TIME_FLEXIBLE:
      case CalendarItemStateEnum.COMMUNAL:
      case CalendarItemStateEnum.AVAILABLE:
        calendarItem = calendarItemsById[calendarItemId.calendarItemId];
        break;
      case CalendarItemStateEnum.LOCKED:
      case CalendarItemStateEnum.HELD:
        calendarItem = find(
          calendarItemsById,
          (calendarItem: CalendarItem) =>
            calendarItem.ticketSubset &&
            calendarItem.ticketSubset.id === calendarItemId.ticketSubsetId
        );
        break;
      case CalendarItemStateEnum.SOLD:
        calendarItem = find(
          calendarItemsById,
          (calendarItem) =>
            calendarItem.purchase &&
            calendarItem.purchase.originalPurchaseId === calendarItemId.originalPurchaseId
        );
        break;
      default:
        assertNever(calendarItemId);
        calendarItem = undefined;
    }

    // This ensures that when editing a booking note, we can fetch the ticket group, get an update and it will surface immediately
    if (calendarItem && ticketGroup && calendarItem.ticketSubset) {
      const ticketSubset: core.TicketSubset | undefined = find(
        ticketGroup.ticketSubset as core.TicketSubset[],
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (subset: core.TicketSubset) => safeToNumber(subset.id) === calendarItem!.ticketSubset.id
      );
      if (ticketSubset) {
        assign(calendarItem.ticketSubset, {
          auditedBookingNote: ticketSubset.auditedBookingNote,
        });
      }
    }
    return calendarItem;
  }
);

export const calendarSelectedTimelineItemSelector = (state: StoreType) =>
  state.calendar.selectedTimelineItem;

export const calendarSelectedTableStateRangeSelector = (state: StoreType) => {
  const { selectedTimelineItem } = state.calendar;
  return isSelectedTableStateRange(selectedTimelineItem) ? selectedTimelineItem : null;
};

export const calendarSelectedSlot = (state: StoreType) => {
  const { selectedTimelineItem } = state.calendar;
  return isSelectedSlot(selectedTimelineItem) ? selectedTimelineItem : null;
};

export const calendarSelectedTableIdSelector = createSelector(
  (state: StoreType) => state.calendar.selectedTableId,
  (selectedTableId?: string): string | undefined => selectedTableId
);

export const calendarSelectedTableIdCalendarSquareIdSelector = createSelector(
  (state: StoreType) => state.calendar.selectedTableIdCalendarSquare,
  (selectedTableIdCalendarSquare?: number): number | undefined => selectedTableIdCalendarSquare
);

export const calendarSelectedTableIdsToBookSelector = createSelector(
  (state: StoreType) => state.calendar.selectedTableIdsToBook,
  (selectedTableIdsToBook) => selectedTableIdsToBook
);

export const selectedCalendarItemIdsSelector = createSelector(
  calendarItemSelectionsSelector,
  calendarSelectedTableIdsToBookSelector,
  calendarSelectedCalendarItemSelector,
  function selectedCalendarItemIDs(
    calendarItemSelections: {
      [calendarItemId: string]: CalendarItem;
    },
    selectedTableIdsToBook?: number[],
    selectedCalendarItem?: CalendarItem
  ) {
    let selectedCalendarItemIds: string[] = [];
    if (selectedTableIdsToBook) {
      selectedCalendarItemIds = Object.keys(calendarItemSelections);
    } else if (isEmpty(calendarItemSelections) && selectedCalendarItem) {
      selectedCalendarItemIds = [selectedCalendarItem.id];
    } else {
      selectedCalendarItemIds = Object.keys(calendarItemSelections);
    }
    return selectedCalendarItemIds;
  }
);

export const calendarItemByIdSelector = createCachedSelector(
  (state: StoreType, calendarItemId: string) => calendarItemId,
  allCalendarItemsByIdSelector,
  (calendarItemId, calendarItems: Dictionary<CalendarItem>) => {
    return calendarItems[calendarItemId];
  }
)((state: StoreType, calendarItemId: string) => calendarItemId);

export const currentPopoverSelector = createSelector(
  (state: StoreType) => state.calendar.popoverType,
  (popoverType?: CalendarPopoverOpenPayload): CalendarPopoverOpenPayload | undefined => popoverType
);
